/**
 * MCP Bridge Manager (Browser Side)
 *
 * Handles WebSocket connection from the browser to the Balancy server
 * for MCP-proxied tool execution. When an external LLM (Claude Desktop,
 * Cursor) calls a browser-proxied tool via MCP, the server forwards
 * the call here, we execute it locally using the same tool executor
 * as the SDK chat, and send the result back.
 */
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const RECONNECT_DELAYS = [1000, 2000, 5000, 10000, 30000]; // Progressive backoff, caps at 30s
// Staleness watchdog: the server pings every ~25s, so if no message arrives
// within this window the socket is a half-open zombie — force a reconnect. This
// is the root-cause fix: a proxy-dropped socket often never fires `onclose`, so
// without this the existing reconnect path is never triggered.
const STALENESS_TIMEOUT_MS = 75000;
const STALENESS_CHECK_MS = 30000;
// ---------------------------------------------------------------------------
// Manager
// ---------------------------------------------------------------------------
export class MCPBridgeManager {
    constructor(config) {
        this.ws = null;
        this.status = 'disconnected';
        this.sessionId = null;
        this.reconnectAttempts = 0;
        this.reconnectTimer = null;
        this.intentionalClose = false;
        this.authFatal = false;
        this.activities = [];
        this.lastMessageAt = 0;
        this.lastCloseCode = null;
        this.stalenessTimer = null;
        this.onWake = () => {
            if (this.intentionalClose || this.authFatal || this.status === 'replaced') {
                return;
            }
            if (this.status === 'connected' || this.status === 'connecting') {
                this.stalenessCheck();
                return;
            }
            // Network/visibility returned while disconnected — reconnect now instead of
            // waiting out the backoff delay.
            this.reconnectAttempts = 0;
            this.clearReconnectTimer();
            this.forceReconnect();
        };
        this.onVisibilityChange = () => {
            if (typeof document !== 'undefined' &&
                document.visibilityState === 'visible') {
                this.onWake();
            }
        };
        this.config = config;
        this.setupLiveness();
    }
    // -------------------------------------------------------------------------
    // Public API
    // -------------------------------------------------------------------------
    /**
     * Connect to the MCP bridge WebSocket.
     */
    connect() {
        if (this.ws &&
            (this.ws.readyState === WebSocket.CONNECTING ||
                this.ws.readyState === WebSocket.OPEN)) {
            return; // Already connected or connecting
        }
        this.intentionalClose = false;
        this.lastMessageAt = Date.now();
        this.setStatus('connecting');
        // Build WebSocket URL with auth params
        const wsProtocol = this.config.serverUrl.startsWith('https') ? 'wss' : 'ws';
        const httpUrl = new URL(this.config.serverUrl);
        const wsUrl = `${wsProtocol}://${httpUrl.host}/ws/mcp?email=${encodeURIComponent(this.config.email)}&token=${encodeURIComponent(this.config.authToken)}`;
        try {
            this.ws = new WebSocket(wsUrl);
            this.ws.onopen = () => this.handleOpen();
            this.ws.onmessage = (event) => this.handleMessage(event);
            this.ws.onclose = (event) => this.handleClose(event);
            this.ws.onerror = () => this.handleError();
        }
        catch (err) {
            console.error('[MCP-Bridge] WebSocket creation failed:', err);
            this.setStatus('disconnected');
            this.scheduleReconnect();
        }
    }
    /**
     * Disconnect from the MCP bridge.
     */
    disconnect() {
        this.intentionalClose = true;
        this.clearReconnectTimer();
        this.teardownLiveness();
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
        this.sessionId = null;
        this.setStatus('disconnected');
    }
    /**
     * Check if the bridge is currently connected.
     */
    isConnected() {
        return this.status === 'connected';
    }
    /**
     * Get the current bridge status.
     */
    getStatus() {
        return this.status;
    }
    /**
     * Get recent tool activity (last 20 entries).
     */
    getActivities() {
        return [...this.activities];
    }
    /**
     * Full connection detail for the status panel — not just the status string but
     * why/how: reconnect progress, last WS close code, and last-seen time.
     */
    getConnectionInfo() {
        return {
            status: this.status,
            reconnectAttempts: this.reconnectAttempts,
            lastCloseCode: this.lastCloseCode,
            lastMessageAt: this.lastMessageAt,
        };
    }
    /**
     * Push a rotated access token into a live bridge.
     *
     * The token is baked into the WS URL at connect time, so without this the
     * bridge keeps dialing with whatever token it was constructed with. The CMS
     * only refreshes reactively — after the old token has already been rejected —
     * so by the time a new token exists, the captured one is already dead.
     */
    updateAuthToken(token) {
        if (!token || token === this.config.authToken)
            return;
        this.config.authToken = token;
        if (this.authFatal) {
            // A fresh token invalidates the terminal auth failure — the reason we
            // stopped retrying is gone, so retry now instead of waiting for a reload.
            this.authFatal = false;
            this.reconnectAttempts = 0;
            this.clearReconnectTimer();
            this.forceReconnect();
            return;
        }
        if (this.status === 'disconnected' && !this.intentionalClose) {
            // Mid-backoff with a stale token: restart now on the new one. Skipped
            // after an intentional disconnect so a re-auth can't revive a closed
            // bridge behind the SDK's back.
            this.reconnectAttempts = 0;
            this.clearReconnectTimer();
            this.forceReconnect();
        }
        // A healthy socket authenticated at connect time and stays valid, so the
        // new token is simply picked up by the next reconnect.
    }
    /**
     * Update config (e.g. when game/branch changes).
     */
    updateConfig(partial) {
        Object.assign(this.config, partial);
        if (this.isConnected() &&
            (partial.gameId !== undefined ||
                partial.branchName !== undefined ||
                partial.simplifiedUrl !== undefined ||
                partial.pageObjectId !== undefined)) {
            // Never re-send bridge_init on a live socket: older servers read it as
            // a new tab and evict this one.
            this.sendPageContextUpdate();
        }
    }
    /**
     * Report what the tab has open now — call on every navigation and game
     * change. Stored in the config too, so a reconnect's bridge_init is current.
     */
    updateContext(context) {
        Object.assign(this.config, context);
        if (this.isConnected()) {
            this.sendPageContextUpdate();
        }
    }
    // -------------------------------------------------------------------------
    // WebSocket event handlers
    // -------------------------------------------------------------------------
    handleOpen() {
        console.log('[MCP-Bridge] WebSocket connected');
        // Do NOT reset reconnectAttempts here — a raw socket open is not proof of a
        // working bridge (server-side auth runs after open). Reset only on the
        // app-level `bridge_connected` ack, or a bad token hot-loops the server ~1/s.
        this.lastMessageAt = Date.now();
        this.sendBridgeInit();
    }
    handleMessage(event) {
        this.lastMessageAt = Date.now();
        try {
            const msg = JSON.parse(event.data);
            this.processMessage(msg);
        }
        catch {
            // Ignore malformed messages
        }
    }
    handleClose(event) {
        this.ws = null;
        this.lastCloseCode = event.code;
        if (this.status === 'replaced') {
            // Don't reconnect — another tab took over
            return;
        }
        if (this.authFatal) {
            // Auth failed — reconnecting would hot-loop the server. Wait for re-auth.
            return;
        }
        if (!this.intentionalClose) {
            // code 1006 = abnormal (proxy reap); 1000/1001 = clean server close.
            console.warn(`[MCP-Bridge] WebSocket closed (code ${event.code}) — scheduling reconnect`);
            this.setStatus('disconnected');
            this.scheduleReconnect();
        }
    }
    handleError() {
        console.error('[MCP-Bridge] WebSocket error');
        // The close handler will deal with reconnection
    }
    // -------------------------------------------------------------------------
    // Message processing
    // -------------------------------------------------------------------------
    processMessage(msg) {
        switch (msg.type) {
            case 'ping':
                // App-level heartbeat from the server — reply so it knows our JS event
                // loop (not just the socket) is alive and can execute tools.
                this.send({ type: 'pong' });
                break;
            case 'bridge_connected':
                this.sessionId = msg.sessionId;
                // The app-level ack is the real proof the bridge works — reset backoff
                // here, not on raw socket open (see handleOpen).
                this.reconnectAttempts = 0;
                this.setStatus('connected');
                console.log(`[MCP-Bridge] Bridge established (session: ${msg.sessionId})`);
                // Navigations while bridge_init was in flight only updated the config
                // (nothing is sent before this ack), so report the current context now.
                this.sendPageContextUpdate();
                break;
            case 'bridge_replaced':
                console.log('[MCP-Bridge] Bridge replaced by another tab');
                this.setStatus('replaced');
                this.ws?.close();
                break;
            case 'tool_execute':
                this.handleToolExecute(msg);
                break;
            case 'tool_activity':
                this.recordActivity({
                    toolName: msg.toolName,
                    status: msg.status,
                    timestamp: msg.timestamp || Date.now(),
                });
                break;
            case 'error':
                console.error('[MCP-Bridge] Server error:', msg.message);
                if (msg.code === 'auth_failed' ||
                    msg.message === 'Authentication failed') {
                    // Fatal: retrying would hammer the server with an S2S token check
                    // ~1×/s forever. Stop and surface for re-auth.
                    this.authFatal = true;
                    this.clearReconnectTimer();
                    this.setStatus('auth_failed');
                }
                break;
        }
    }
    async handleToolExecute(msg) {
        const { requestId, toolName, arguments: args } = msg;
        try {
            const result = await this.config.toolExecutor(toolName, args || {});
            this.send({
                type: 'tool_result',
                requestId,
                success: result.success,
                result: result.success ? result.result : undefined,
                error: result.success ? undefined : result.error,
            });
        }
        catch (err) {
            this.send({
                type: 'tool_result',
                requestId,
                success: false,
                error: err?.message || 'Tool execution failed',
            });
        }
    }
    // -------------------------------------------------------------------------
    // Helpers
    // -------------------------------------------------------------------------
    sendBridgeInit() {
        this.send({
            type: 'bridge_init',
            gameId: this.config.gameId,
            branchName: this.config.branchName,
            simplifiedUrl: this.config.simplifiedUrl ?? null,
            pageObjectId: this.config.pageObjectId ?? null,
        });
    }
    sendPageContextUpdate() {
        // Servers that predate the gameId/branchName keys ignore them.
        this.send({
            type: 'page_context_update',
            gameId: this.config.gameId ?? null,
            branchName: this.config.branchName ?? null,
            simplifiedUrl: this.config.simplifiedUrl ?? null,
            pageObjectId: this.config.pageObjectId ?? null,
        });
    }
    send(data) {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(data));
        }
    }
    setStatus(status) {
        if (this.status !== status) {
            this.status = status;
            this.config.onStatusChange?.(status);
        }
    }
    recordActivity(activity) {
        this.activities.push(activity);
        // Keep last 20
        if (this.activities.length > 20) {
            this.activities = this.activities.slice(-20);
        }
        this.config.onActivity?.(activity);
    }
    scheduleReconnect() {
        if (this.intentionalClose || this.authFatal) {
            return;
        }
        if (this.reconnectTimer) {
            return; // Already scheduled
        }
        const delay = RECONNECT_DELAYS[Math.min(this.reconnectAttempts, RECONNECT_DELAYS.length - 1)];
        this.reconnectAttempts++;
        // Retry indefinitely at the capped 30s backoff — a dropped bridge must always
        // recover without a page reload. The old 10-attempt cap gave up after ~3.3min
        // (shorter than a routine deploy), which is why bridges stayed dead.
        console.log(`[MCP-Bridge] Reconnecting in ${delay / 1000}s (attempt ${this.reconnectAttempts})`);
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            this.connect();
        }, delay);
    }
    clearReconnectTimer() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }
    // -------------------------------------------------------------------------
    // Liveness: staleness watchdog + wake-on-network/visibility
    // -------------------------------------------------------------------------
    setupLiveness() {
        this.stalenessTimer = setInterval(() => this.stalenessCheck(), STALENESS_CHECK_MS);
        if (typeof window !== 'undefined') {
            window.addEventListener('online', this.onWake);
            window.addEventListener('focus', this.onWake);
        }
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', this.onVisibilityChange);
        }
    }
    teardownLiveness() {
        if (this.stalenessTimer) {
            clearInterval(this.stalenessTimer);
            this.stalenessTimer = null;
        }
        if (typeof window !== 'undefined') {
            window.removeEventListener('online', this.onWake);
            window.removeEventListener('focus', this.onWake);
        }
        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this.onVisibilityChange);
        }
    }
    stalenessCheck() {
        // Detects a half-open zombie: we think we're connected but the server (which
        // pings every ~25s) has gone silent. Reconnection while already disconnected
        // is owned by scheduleReconnect, so this only acts on a live-looking socket.
        if (this.intentionalClose || this.authFatal || this.status === 'replaced') {
            return;
        }
        if ((this.status === 'connected' || this.status === 'connecting') &&
            Date.now() - this.lastMessageAt > STALENESS_TIMEOUT_MS) {
            console.warn('[MCP-Bridge] No server messages within staleness window — forcing reconnect');
            this.forceReconnect();
        }
    }
    forceReconnect() {
        // The zombie socket is stuck in readyState OPEN, so connect() would
        // early-return. Detach its handlers (so a late close can't trigger a second
        // reconnect), close it best-effort, drop the reference, then connect fresh.
        const old = this.ws;
        if (old) {
            old.onopen = null;
            old.onmessage = null;
            old.onclose = null;
            old.onerror = null;
            try {
                old.close();
            }
            catch {
                // ignore
            }
        }
        this.ws = null;
        this.setStatus('disconnected');
        this.connect();
    }
}
